import { useState, useEffect, useCallback } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ScriptsLibraryTab = () => {
  const [scripts, setScripts] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [q, setQ] = useState("");
  const [activeTag, setActiveTag] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { script_id, title, content, tags }
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [toast, setToast] = useState("");

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2200);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (activeTag) params.set("tag", activeTag);
      const r = await fetch(`${BACKEND_URL}/api/ai/scripts?${params}`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setScripts(d.scripts || []);
        setAllTags(d.all_tags || []);
      }
    } catch {}
    setLoading(false);
  }, [q, activeTag]);

  useEffect(() => { load(); }, [load]);

  const startCreate = () => {
    setEditing({ script_id: null, title: "", content: "", tags: "" });
    setCreating(true);
  };

  const startEdit = (s) => {
    setEditing({
      script_id: s.script_id,
      title: s.title,
      content: s.content,
      tags: (s.tags || []).join(", "),
    });
    setCreating(false);
  };

  const saveEdit = async () => {
    if (!editing.title.trim() || !editing.content.trim()) return;
    setBusy(true);
    try {
      const tags = editing.tags.split(",").map(t => t.trim()).filter(Boolean);
      const url = creating ? `${BACKEND_URL}/api/ai/scripts` : `${BACKEND_URL}/api/ai/scripts/${editing.script_id}`;
      const method = creating ? "POST" : "PUT";
      const r = await fetch(url, {
        method,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: editing.title, content: editing.content, tags }),
      });
      if (r.ok) {
        setEditing(null);
        showToast(creating ? "Script created ✓" : "Script updated ✓");
        load();
      }
    } catch {}
    setBusy(false);
  };

  const deleteScript = async (script_id) => {
    if (!confirm("Delete this script? This can't be undone.")) return;
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/ai/scripts/${script_id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (r.ok) {
        showToast("Script deleted");
        load();
      }
    } catch {}
    setBusy(false);
  };

  const copyScript = async (content) => {
    try {
      await navigator.clipboard.writeText(content);
      showToast("Copied to clipboard ✓");
    } catch {}
  };

  return (
    <div data-testid="scripts-library-tab" style={{ padding: "0 0.5rem" }}>
      {/* Header */}
      <div style={{ marginBottom: "1.25rem", display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "0.85rem" }}>
        <div>
          <h1 style={{ color: "#fff", fontSize: "1.65rem", fontWeight: 800, margin: "0 0 0.4rem", letterSpacing: "-0.01em" }}>
            Scripts Library
          </h1>
          <p style={{ color: "#ADBED1", fontSize: "0.92rem", margin: 0, lineHeight: 1.5, maxWidth: 760 }}>
            Saved snippets you (or Moovin AI) wrote — owner email templates, walkthrough one-liners, tax explainers, pitch language. <strong style={{ color: "#FFB347" }}>Click any reply in Moovin AI&apos;s chat and hit 💾 Save as Script to add to this library.</strong>
          </p>
        </div>
        <button onClick={startCreate} data-testid="btn-new-script" style={{ padding: "0.6rem 1.15rem", background: "linear-gradient(135deg, #00DC78 0%, #00FFFF 100%)", border: "none", color: "#030B1A", borderRadius: 6, fontWeight: 800, fontSize: "0.82rem", cursor: "pointer", whiteSpace: "nowrap" }}>
          + New Script
        </button>
      </div>

      {/* Search + Tags */}
      <div style={{ display: "flex", gap: "0.6rem", marginBottom: "1rem", flexWrap: "wrap", alignItems: "center" }}>
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="🔍 Search title or content…"
          data-testid="scripts-search"
          style={{ flex: 1, minWidth: 240, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.12)", color: "#fff", padding: "0.55rem 0.85rem", borderRadius: 6, fontSize: "0.88rem" }}
        />
        {activeTag && (
          <button onClick={() => setActiveTag("")} data-testid="clear-tag" style={{ background: "rgba(255,139,107,0.10)", border: "1px solid rgba(255,139,107,0.30)", color: "#FF8B6B", padding: "0.4rem 0.7rem", borderRadius: 5, fontSize: "0.76rem", fontWeight: 700, cursor: "pointer" }}>
            ✕ {activeTag}
          </button>
        )}
      </div>

      {allTags.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "1.1rem" }}>
          {allTags.map(t => (
            <button
              key={t}
              onClick={() => setActiveTag(activeTag === t ? "" : t)}
              data-testid={`tag-${t.replace(/\s+/g, '-')}`}
              style={{
                background: activeTag === t ? "rgba(0,255,255,0.15)" : "rgba(255,255,255,0.03)",
                border: `1px solid ${activeTag === t ? "rgba(0,255,255,0.35)" : "rgba(255,255,255,0.08)"}`,
                color: activeTag === t ? "#00FFFF" : "#ADBED1",
                padding: "0.35rem 0.75rem",
                borderRadius: 5,
                fontSize: "0.74rem",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {t}
            </button>
          ))}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div style={{ color: "#7E92A8", textAlign: "center", padding: "2rem" }}>Loading…</div>
      ) : scripts.length === 0 ? (
        <div data-testid="scripts-empty" style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "2rem 1.5rem", textAlign: "center" }}>
          <div style={{ color: "#fff", fontSize: "1rem", fontWeight: 700, marginBottom: "0.5rem" }}>No scripts saved yet.</div>
          <div style={{ color: "#7E92A8", fontSize: "0.85rem", lineHeight: 1.55, maxWidth: 460, margin: "0 auto" }}>
            Open Moovin AI (✦ button bottom-right), ask for an owner email, walkthrough line, or tax explainer — then hit <strong style={{ color: "#00DC78" }}>💾 Save as Script</strong> on the reply. Or click <strong style={{ color: "#00FFFF" }}>+ New Script</strong> above to write one yourself.
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))", gap: "1rem" }}>
          {scripts.map(s => (
            <div key={s.script_id} data-testid={`script-card-${s.script_id}`} style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "1rem 1.1rem", display: "flex", flexDirection: "column" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.5rem" }}>
                <h3 style={{ color: "#fff", fontSize: "0.95rem", fontWeight: 800, margin: 0, flex: 1, lineHeight: 1.35 }}>{s.title}</h3>
              </div>
              {s.tags && s.tags.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem", marginBottom: "0.55rem" }}>
                  {s.tags.map(t => (
                    <span key={t} onClick={() => setActiveTag(t)} style={{ background: "rgba(0,255,255,0.08)", border: "1px solid rgba(0,255,255,0.20)", color: "#5AC8D8", padding: "0.15rem 0.45rem", borderRadius: 4, fontSize: "0.66rem", fontWeight: 700, cursor: "pointer" }}>
                      {t}
                    </span>
                  ))}
                </div>
              )}
              <div style={{ color: "#ADBED1", fontSize: "0.82rem", lineHeight: 1.5, whiteSpace: "pre-wrap", maxHeight: 180, overflowY: "auto", marginBottom: "0.85rem", flex: 1, padding: "0.55rem 0.7rem", background: "rgba(0,0,0,0.20)", borderRadius: 6, border: "1px solid rgba(255,255,255,0.04)" }}>
                {s.content}
              </div>
              <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", alignItems: "center" }}>
                <button onClick={() => copyScript(s.content)} data-testid={`btn-copy-${s.script_id}`} style={{ background: "linear-gradient(135deg, #00DC78 0%, #00FFFF 100%)", border: "none", color: "#030B1A", padding: "0.5rem 1.1rem", borderRadius: 6, fontSize: "0.82rem", fontWeight: 800, cursor: "pointer", flex: 1 }}>📋 Copy</button>
                <details style={{ position: "relative" }}>
                  <summary data-testid={`btn-more-${s.script_id}`} style={{ listStyle: "none", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.12)", color: "#ADBED1", padding: "0.5rem 0.7rem", borderRadius: 6, fontSize: "0.82rem", fontWeight: 700, cursor: "pointer", userSelect: "none" }}>⋯</summary>
                  <div style={{ position: "absolute", right: 0, top: "calc(100% + 4px)", background: "#030B1A", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, boxShadow: "0 8px 24px rgba(0,0,0,0.5)", minWidth: 130, zIndex: 10, overflow: "hidden" }}>
                    <button onClick={() => startEdit(s)} data-testid={`btn-edit-${s.script_id}`} style={{ display: "block", width: "100%", textAlign: "left", background: "transparent", border: "none", color: "#5AC8D8", padding: "0.55rem 0.85rem", fontSize: "0.78rem", fontWeight: 700, cursor: "pointer" }}>✏️ Edit</button>
                    <button onClick={() => deleteScript(s.script_id)} data-testid={`btn-delete-${s.script_id}`} style={{ display: "block", width: "100%", textAlign: "left", background: "transparent", border: "none", color: "#FF8B6B", padding: "0.55rem 0.85rem", fontSize: "0.78rem", fontWeight: 700, cursor: "pointer", borderTop: "1px solid rgba(255,255,255,0.06)" }}>🗑️ Delete</button>
                  </div>
                </details>
              </div>
              <div style={{ color: "#5A7090", fontSize: "0.66rem", marginTop: "0.55rem", textAlign: "right" }}>
                {s.updated_at ? `Updated ${new Date(s.updated_at).toLocaleDateString()}` : ""}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit / Create modal */}
      {editing && (
        <div onClick={() => !busy && setEditing(null)} style={{ position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
          <div onClick={e => e.stopPropagation()} style={{ width: "min(620px, 100%)", background: "#030B1A", border: "1px solid rgba(0,255,255,0.30)", borderRadius: 12, padding: "1.4rem 1.5rem", maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <h3 style={{ color: "#fff", fontSize: "1.05rem", fontWeight: 800, margin: 0 }}>{creating ? "+ New Script" : "✏️ Edit Script"}</h3>
              <button onClick={() => !busy && setEditing(null)} style={{ background: "transparent", border: "none", color: "#ADBED1", fontSize: "1.2rem", cursor: "pointer" }}>×</button>
            </div>

            <label style={{ color: "#7E92A8", fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", display: "block", marginBottom: "0.3rem" }}>Title</label>
            <input value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} data-testid="edit-modal-title" style={{ width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.15)", color: "#fff", padding: "0.55rem 0.7rem", borderRadius: 6, fontSize: "0.88rem", marginBottom: "0.85rem" }} />

            <label style={{ color: "#7E92A8", fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", display: "block", marginBottom: "0.3rem" }}>Tags (comma-separated)</label>
            <input value={editing.tags} onChange={e => setEditing({ ...editing, tags: e.target.value })} data-testid="edit-modal-tags" placeholder="Tax, Owner Email, Walkthrough…" style={{ width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.15)", color: "#fff", padding: "0.55rem 0.7rem", borderRadius: 6, fontSize: "0.85rem", marginBottom: "0.85rem" }} />

            <label style={{ color: "#7E92A8", fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", display: "block", marginBottom: "0.3rem" }}>Content</label>
            <textarea value={editing.content} onChange={e => setEditing({ ...editing, content: e.target.value })} rows={12} data-testid="edit-modal-content" style={{ width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.15)", color: "#fff", padding: "0.55rem 0.7rem", borderRadius: 6, fontSize: "0.84rem", lineHeight: 1.5, fontFamily: "inherit", resize: "vertical", marginBottom: "1rem" }} />

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
              <button onClick={() => !busy && setEditing(null)} disabled={busy} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.15)", color: "#ADBED1", padding: "0.55rem 1rem", borderRadius: 6, fontSize: "0.82rem", cursor: busy ? "wait" : "pointer", fontWeight: 700 }}>Cancel</button>
              <button onClick={saveEdit} disabled={busy || !editing.title.trim() || !editing.content.trim()} data-testid="edit-modal-save" style={{ background: busy || !editing.title.trim() || !editing.content.trim() ? "rgba(255,255,255,0.08)" : "linear-gradient(135deg, #00DC78 0%, #00FFFF 100%)", border: "none", color: "#030B1A", padding: "0.55rem 1.1rem", borderRadius: 6, fontWeight: 800, fontSize: "0.85rem", cursor: busy || !editing.title.trim() || !editing.content.trim() ? "not-allowed" : "pointer" }}>
                {busy ? "Saving…" : creating ? "Create" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div data-testid="scripts-toast" style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", zIndex: 10001, background: "linear-gradient(135deg, #00DC78 0%, #00FFFF 100%)", color: "#030B1A", padding: "0.65rem 1.2rem", borderRadius: 8, fontWeight: 800, fontSize: "0.85rem", boxShadow: "0 6px 20px rgba(0,220,120,0.4)" }}>
          {toast}
        </div>
      )}
    </div>
  );
};

export default ScriptsLibraryTab;
